<script setup lang="ts">
/**
 * FittingsBrowser — Left-panel filters for public fittings
 * Dropdowns for class, hull, sort, price range.
 * Results shown in BrowseView (center panel) via shared Pinia store.
 */
import { ref, onMounted } from 'vue';
import { useFitStore } from '../../stores/fitStore';

const store = useFitStore();

let searchTimeout: ReturnType<typeof setTimeout> | null = null;
function handleSearchInput(e: Event): void {
  const q = (e.target as HTMLInputElement).value;
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => store.browseSetSearch(q), 350);
}

function onClassChange(e: Event): void {
  store.browseSetClass((e.target as HTMLSelectElement).value ? parseInt((e.target as HTMLSelectElement).value, 10) : null);
}

function onHullChange(e: Event): void {
  store.browseSetHull((e.target as HTMLSelectElement).value ? parseInt((e.target as HTMLSelectElement).value, 10) : null);
}

function onSortChange(e: Event): void {
  const val = (e.target as HTMLSelectElement).value;
  const [by, order] = val.split('-');
  store.browseSetSort(by, order);
}

const minPriceInput = ref('');
const maxPriceInput = ref('');
let priceTimeout: ReturnType<typeof setTimeout> | null = null;

function handlePriceChange(): void {
  if (priceTimeout) clearTimeout(priceTimeout);
  priceTimeout = setTimeout(() => {
    const min = minPriceInput.value ? parseFloat(minPriceInput.value) * 1_000_000 : null;
    const max = maxPriceInput.value ? parseFloat(maxPriceInput.value) * 1_000_000 : null;
    store.browseSetPriceRange(min, max);
  }, 500);
}

onMounted(() => {
  if (store.browseShipClasses.length === 0) {
    store.browseInit();
  }
});
</script>

<template>
  <div class="filter-panel">
    <h3 class="panel-title">Filters</h3>

    <div class="filter-group">
      <label class="filter-label">Search</label>
      <input
        type="text"
        class="filter-input"
        placeholder="Fit name or ship..."
        :value="store.browseSearch"
        @input="handleSearchInput"
      />
    </div>

    <div class="filter-group">
      <label class="filter-label">Ship Class</label>
      <select class="filter-select" :value="store.browseClassID ?? ''" @change="onClassChange">
        <option value="">All Classes</option>
        <option v-for="sc in store.browseShipClasses" :key="sc.groupID" :value="sc.groupID">
          {{ sc.groupName }}
        </option>
      </select>
    </div>

    <div v-if="store.browseHulls.length > 0" class="filter-group">
      <label class="filter-label">Hull</label>
      <select class="filter-select" :value="store.browseHullTypeID ?? ''" @change="onHullChange">
        <option value="">All Hulls</option>
        <option v-for="hull in store.browseHulls" :key="hull.typeID" :value="hull.typeID">
          {{ hull.typeName }}
        </option>
      </select>
    </div>

    <div class="filter-group">
      <label class="filter-label">Sort By</label>
      <select class="filter-select" :value="`${store.browseSortBy}-${store.browseSortOrder}`" @change="onSortChange">
        <option value="date-desc">Newest First</option>
        <option value="date-asc">Oldest First</option>
        <option value="name-asc">Name A-Z</option>
        <option value="name-desc">Name Z-A</option>
        <option value="ship-asc">Ship A-Z</option>
        <option value="ship-desc">Ship Z-A</option>
        <option value="price-desc">Most Expensive</option>
        <option value="price-asc">Cheapest</option>
      </select>
    </div>

    <div class="filter-group">
      <label class="filter-label">Price Range (M ISK)</label>
      <div class="price-inputs">
        <input
          type="number"
          class="filter-input price-input"
          placeholder="Min"
          v-model="minPriceInput"
          @input="handlePriceChange"
          min="0"
          step="1"
        />
        <span class="price-sep">&ndash;</span>
        <input
          type="number"
          class="filter-input price-input"
          placeholder="Max"
          v-model="maxPriceInput"
          @input="handlePriceChange"
          min="0"
          step="1"
        />
      </div>
    </div>

    <button class="clear-btn" @click="store.browseClearFilters(); minPriceInput = ''; maxPriceInput = ''">
      Clear All Filters
    </button>

    <div class="result-count" v-if="!store.browseLoading && store.browseResponse">
      {{ store.browseTotalFits.toLocaleString() }} fitting{{ store.browseTotalFits !== 1 ? 's' : '' }}
    </div>
  </div>
</template>

<style scoped>
.filter-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: 14px;
  gap: 12px;
}

.panel-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-color);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-label {
  font-size: 0.72rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 500;
}

.filter-input,
.filter-select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-darker);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: inherit;
  transition: border-color var(--transition-fast);
}

.filter-select {
  cursor: pointer;
  appearance: auto;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--accent-blue);
  box-shadow: 0 0 0 1px rgba(74, 158, 255, 0.2);
}

.price-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.price-input {
  flex: 1;
  min-width: 0;
}

.price-sep {
  color: var(--text-secondary);
  font-size: 0.85rem;
  flex-shrink: 0;
}

/* Remove number input spinners */
.price-input::-webkit-outer-spin-button,
.price-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.price-input[type=number] { -moz-appearance: textfield; }

.clear-btn {
  padding: 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-btn:hover {
  color: var(--text-primary);
  border-color: var(--accent-red);
  background: rgba(244, 67, 54, 0.06);
}

.result-count {
  font-size: 0.85rem;
  color: var(--accent-blue);
  font-weight: 500;
  padding-top: 8px;
  border-top: 1px solid var(--border-color);
}
</style>
